import type { SourceChannelDto } from '@upme/contracts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@upme/ui/components/alert-dialog';
import { Button } from '@upme/ui/components/button';
import { Input } from '@upme/ui/components/input';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDeleteSourceChannel } from '../mutations';

interface Props {
  channel: SourceChannelDto;
  onDeleted: () => void;
}

export function DeleteSourceChannelDialog({ channel, onDeleted }: Props) {
  const { t } = useTranslation('source-channels');
  const [confirm, setConfirm] = useState('');
  const del = useDeleteSourceChannel();
  const canConfirm = confirm === channel.slug;

  const handleConfirm = async () => {
    try {
      await del.mutateAsync(channel.id);
      toast.success(t('delete.success'));
      onDeleted();
    } catch (err) {
      const message = err instanceof Error ? err.message : t('delete.error');
      toast.error(message);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="destructive">{t('delete.button')}</Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('delete.confirmTitle', { title: channel.title })}</AlertDialogTitle>
          <AlertDialogDescription>{t('delete.confirmDescription')}</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="my-4">
          <p className="text-sm mb-2">
            {t('delete.confirmHintPrefix')}
            <code className="bg-muted px-1 rounded">{channel.slug}</code>
            {t('delete.confirmHintSuffix')}
          </p>
          <Input
            placeholder={t('delete.slugPlaceholder')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('delete.cancel')}</AlertDialogCancel>
          <AlertDialogAction disabled={!canConfirm || del.isPending} onClick={handleConfirm}>
            {del.isPending ? t('delete.deletingEllipsis') : t('delete.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
